// #region Imports
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import MuiDrawer from "@mui/material/Drawer";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { styled } from "@mui/material/styles";
import { SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FeeScheduleCategoryList from "_shared/fee-schedules/categories/_views/fee-schedule-category-list";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
// The marketplace owns what a team advertises; this panel only offers it. `_shared` already reaches
// into marketplace for the same reason in `edit-fee-schedule-category.tsx`.
import TeamPublishedRatesList from "marketplace/teams/fee-schedules/_views/team-published-rates-list";
import { useState } from "react";
import { FeeScheduleCategoryMaskedCurrency, MaskedFeeSchedule } from "work/masked-fields";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";
// #endregion

// #region Styled Components
const Drawer = styled(MuiDrawer)(() => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    // Ends above the assistant's bar below lg, which stays reachable while the panel is open (#6128).
    height: `calc(100% - 64px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
    top: `calc(64px + ${shellTopBannersHeight()})`,
    width: SIDE_PANEL_WIDTH,
    boxSizing: 'border-box',
  },
}));
const FeeCategoryPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition"
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    width: open ? SIDE_PANEL_WIDTH : 0,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open
        ? theme.transitions.easing.easeOut
        : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}));
const FeeCategoryPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
  minHeight: 0,
}));
const FeeCategoryPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  height: "100%",
  minHeight: 0,
  padding: theme.spacing(2),
  // Fills the paper rather than restating its width, which would put the padding outside the panel.
  boxSizing: "border-box",
  width: "100%",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
const SourceTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1),
  minHeight: 0,
}));
// #endregion

// #region Props/Types
type ExistingFeeScheduleCategoryDrawerProps = {
  selectedCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
  /** Proposal context, when opened from a proposal fee-schedule editor, for need-to-know catalog access. */
  proposalId?: string;
  /**
   * The marketplace team the proposal is with, when it came from one (`VendorTeam.marketplaceId`).
   *
   * Its presence is what adds the team's advertised rates as a second source (issue #5113). Absent
   * for a proposal drafted against a team that was not picked off the marketplace, and the panel is
   * then exactly the single-source catalog it was — there is nothing to offer and no tab to show.
   */
  marketplaceTeamId?: Guid;
};

/** Which list the panel is showing. The catalog is the default, as it was the only source before. */
type CategorySource = "catalog" | "team";
// #endregion

export default function ExistingFeeScheduleCategoryDrawer(props: Readonly<ExistingFeeScheduleCategoryDrawerProps>) {
  const {
    selectedCategories,
    isOpen,
    onClose,
    onCategoriesSelected,
    proposalId,
    marketplaceTeamId,
  } = props;

  const [source, setSource] = useState<CategorySource>("catalog");

  // A proposal that is not with a marketplace team has one source, so it is not offered a choice.
  const offersTeamRates = Boolean(marketplaceTeamId);
  const activeSource: CategorySource = offersTeamRates ? source : "catalog";

  // #region Render
  return (
    <FeeCategoryPanel
      open={isOpen}
      anchor="right"
      variant="persistent"
      closeAfterTransition={false}
      // What a page's side-panel column reads to stop clipping an open paper. See `ProposalSidePanelColumn`.
      slotProps={{ paper: { "data-open": isOpen ? "true" : undefined } as object }}
    >
      <FeeCategoryPanelContainer>
        <FeeCategoryPanelContent>
          <TitleBar>
            <Typography variant="h5">Select Existing Categories</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          {offersTeamRates && (
            <SourceTabs
              value={activeSource}
              onChange={(_event, value: CategorySource) => setSource(value)}
              aria-label="Category source"
            >
              <Tab value="catalog" label="Your Catalog" />
              <Tab value="team" label="Team's Published Rates" />
            </SourceTabs>
          )}
          {isOpen && activeSource === "catalog" && (
            <FeeScheduleCategoryList
              disabledCategories={selectedCategories}
              onCategoriesSelected={onCategoriesSelected}
              onCancel={onClose}
              proposalId={proposalId}
            />
          )}
          {isOpen && activeSource === "team" && marketplaceTeamId && (
            <TeamPublishedRatesList
              teamId={marketplaceTeamId}
              disabledCategories={selectedCategories}
              onCategoriesSelected={onCategoriesSelected}
              onCancel={onClose}
            />
          )}
        </FeeCategoryPanelContent>
      </FeeCategoryPanelContainer>
    </FeeCategoryPanel>
  );
}
// #endregion
